import styled, { css, keyframes } from 'styled-components'

// The card that's *becoming* focused always comes from depth 1 (the
// immediate neighbor, regardless of whether you pressed next or prev), and
// the card that's *leaving* focus always settles at depth 1 too — so both
// arcs can be hardcoded to match that exact resting transform, matching the
// depth formula in `Card` below (0.7rem / 0.045 per depth).
//
// --swap-dir (1 for "next", -1 for "prev") flips which side each arc bows
// to, so "prev" renders as the exact mirror of "next": next sends the
// exiting card left and pulls the entering one in from the right; prev
// does the reverse (entering card comes from the left, exiting card
// recedes to the right).
const enterArc = keyframes`
  0% {
    transform: translateY(0.7rem) scale(0.955);
    opacity: 0.88;
  }
  55% {
    transform: translateX(calc(var(--swap-dir, 1) * 1.75rem)) translateY(0.4rem)
      scale(0.99);
    opacity: 1;
  }
  100% {
    transform: translateX(0) translateY(0) scale(1);
    opacity: 1;
  }
`

const exitArc = keyframes`
  0% {
    transform: translateX(0) translateY(0) scale(1);
    opacity: 1;
  }
  45% {
    transform: translateX(calc(var(--swap-dir, 1) * -1.75rem)) translateY(0.4rem)
      scale(0.99);
    opacity: 1;
  }
  100% {
    transform: translateY(0.7rem) scale(0.955);
    opacity: 0.88;
  }
`

export const Stack = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 1rem;
`

// Caller sizes the stack via --card-stack-width / --card-stack-height
// (defaults to 100% of the parent).
export const CardsWindow = styled.div`
  position: relative;
  width: var(--card-stack-width, 100%);
  max-width: var(--card-stack-width, 100%);
  height: var(--card-stack-height, 100%);
`

export const Card = styled.div`
  ${({ theme }) => css`
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    /* Anchor shrinking to the bottom edge. With the origin at the top,
       the scale-driven shrink pulls the bottom edge UP faster than the
       small translateY pushes it down, so the card behind ends up fully
       hidden instead of peeking. Anchoring at the bottom keeps that edge
       fixed regardless of scale — translateY alone controls how much of
       it shows below the card in front. */
    transform-origin: bottom center;
    background-color: ${theme.colors.white};
    border-radius: 1rem;
    /* Depth cue: the focused card (--depth: 0) gets the strongest shadow;
       it fades out subtly the further back a card sits (shadow only —
       cards themselves stay fully opaque). */
    box-shadow: 0 0.5rem 1.5rem
      rgba(0, 0, 0, calc(0.18 - var(--depth, 0) * 0.12));
    transition:
      transform 280ms ease-out,
      opacity 280ms ease-out;
    /* Cards behind peek from the bottom edge only — straight down, no
       sideways drift — getting a little smaller the further back they
       sit. The focused card (--depth: 0) resolves to no offset. */
    transform: translateY(calc(var(--depth, 0) * 0.7rem))
      scale(calc(1 - var(--depth, 0) * 0.045));
    opacity: 1;
    z-index: calc(10 - var(--depth, 0));

    &[data-focused='false'] {
      pointer-events: none;
    }
  `}

  /* The card becoming focused always comes from depth 1, and the one
     leaving focus always settles at depth 1 — a brief left/right arc on
     just that pair reads as "swapping", while every other depth shift
     among background cards keeps the plain transition above. Skipped on
     first mount (the [data-animate] ancestor flag) so opening the modal
     doesn't play a little dance. */
  [data-animate='true'] > &[data-focused='true'] {
    animation: ${enterArc} 280ms ease-out;
  }

  [data-animate='true'] > &[data-focused='false'][data-depth='1'] {
    animation: ${exitArc} 280ms ease-out;
  }
`

export const ArrowButton = styled.button`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 2.5rem;
    height: 2.5rem;
    border: none;
    border-radius: 50%;
    background: ${theme.colors.white};
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
    cursor: pointer;

    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    svg {
      font-size: 1.25rem;
    }
  `}
`
